import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BackNavigationBar } from "@/components/ui/BackNavigationBar";
import { Card, CardContent } from "@/components/ui/Card";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { AppointmentDetailsCard } from "./components/AppointmentDetailsCard";
import { useUser } from "@/api/hooks/user.hooks";
import {
  useCustomerProfile,
  useCustomerPaymentMethod,
  paymentMethodQueryKey,
} from "@/api/hooks/customer.hooks";
import { useRequestAppointmentMutation } from "@/api/hooks/appointments.hooks";
import { AuthRequiredError, SessionExpiredError } from "@/auth/errors/authErrors";
import { authCoordinator } from "@/auth/authCoordinator";
import {
  CardCollector,
  type CardCollectorHandle,
} from "@/features/payment/components/CardCollector";
import { SavedCardSummary } from "@/features/payment/components/SavedCardSummary";
import { VisitType } from "@/api/enums";
import { bookingFormSchema } from "@/api/schemas/booking.schema";
import { getErrorMessage } from "@/api/hooks/useMutationWithToast";
import { STORAGE_KEYS } from "@/constants/storage";

const phoneMask = (d: string) =>
  !d
    ? ""
    : d.length <= 3
      ? `(${d}`
      : d.length <= 6
        ? `(${d.slice(0, 3)}) ${d.slice(3)}`
        : `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;

// The API rejects a booking whose slot was taken in the meantime, or whose slot
// disagrees with the specialist it was rendered under. Either way the patient's
// pick is stale and the only way forward is choosing another time.
const isStaleSlotError = (error: unknown) =>
  getErrorMessage(error).startsWith("The selected time slot");

function FieldError({ message }: { message?: string }) {
  return (
    <p className={`text-xs h-4 leading-4 text-destructive transition-opacity ${message ? "opacity-100" : "opacity-0"}`}>
      {message ?? "\u00A0"}
    </p>
  );
}

export default function BookingRequest() {
  const navigate = useNavigate();
  const params = useSearch({ from: "/booking" });
  const { data: user } = useUser();
  const { data: profile } = useCustomerProfile({ enabled: !!user });
  const isLoggedIn = !!user;
  const { data: paymentMethod, isSuccess: paymentMethodKnown } =
    useCustomerPaymentMethod({ enabled: isLoggedIn });
  const createAppointment = useRequestAppointmentMutation();

  const queryClient = useQueryClient();

  const cardRef = useRef<CardCollectorHandle>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [savingCard, setSavingCard] = useState(false);
  const [isReplacingCard, setIsReplacingCard] = useState(false);

  // A logged-in patient with no card on file must add one before their first
  // appointment. Anonymous users add it after logging in (the form re-renders
  // this section once they return authenticated). Until the summary resolves we
  // don't yet know, so booking waits rather than skipping the gate.
  const hasCard = isLoggedIn && paymentMethodKnown && !!paymentMethod?.hasPaymentMethod;
  const needsCard = isLoggedIn && paymentMethodKnown && !paymentMethod?.hasPaymentMethod;
  const paymentResolved = !isLoggedIn || paymentMethodKnown;
  // The card form is mounted either because there's no card yet, or because the
  // patient chose to replace the one on file. Both paths confirm on submit.
  const collectingCard = needsCard || isReplacingCard;

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    symptoms: "",
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [touched, setTouched] = useState<Set<string>>(new Set());

  const smsConsentPrefilled = useRef(false);

  useEffect(() => {
    if (profile && !smsConsentPrefilled.current) {
      smsConsentPrefilled.current = true;
      setSmsConsent(profile.smsOptIn ?? false);
    }
  }, [profile]);

  const email = user?.email || form.email;

  // Restore what the patient already typed if they were sent to login (e.g. to
  // add a card) and bounced back to this form. Only personal fields are hydrated
  // — the appointment itself comes from the URL search params.
  const pendingHydrated = useRef(false);
  useEffect(() => {
    if (pendingHydrated.current) return;
    pendingHydrated.current = true;
    const pending = authCoordinator.getPendingBooking();
    if (!pending) return;
    setForm((prev) => ({
      ...prev,
      firstName: prev.firstName || pending.firstName || "",
      lastName: prev.lastName || pending.lastName || "",
      phone:
        prev.phone ||
        (pending.userPhoneNumber ?? "").replace(/\D/g, "").slice(0, 10),
      dateOfBirth: prev.dateOfBirth || pending.dateOfBirth || "",
      symptoms: prev.symptoms || pending.symptoms || "",
    }));
    setSmsConsent((p) => p || !!pending.smsConsent);
  }, []);

  // Seed editable fields from the customer profile when it loads. The profile
  // can contain stale or wrong data (e.g. email landed in firstName from an OTP
  // signup), so we let the user correct it instead of locking the value down.
  useEffect(() => {
    if (!profile) return;
    setForm((prev) => ({
      ...prev,
      firstName: prev.firstName || profile.firstName || "",
      lastName: prev.lastName || profile.lastName || "",
      phone: prev.phone || (profile.phoneNumber ?? "").replace(/\D/g, "").slice(0, 10),
      dateOfBirth: prev.dateOfBirth || profile.dateOfBirth || "",
    }));
  }, [profile]);

  const validation = bookingFormSchema.safeParse({
    firstName: form.firstName,
    lastName: form.lastName,
    email,
    phoneNumber: form.phone,
    dateOfBirth: form.dateOfBirth,
    symptoms: form.symptoms,
  });
  const fieldErrors: Record<string, string[] | undefined> = validation.success
    ? {}
    : z.flattenError(validation.error).fieldErrors;
  const isValid = validation.success;

  const blur = (f: string) => () => setTouched((prev) => new Set(prev).add(f));
  const err = (f: string) => (touched.has(f) ? fieldErrors[f]?.[0] : undefined);

  // Re-submitting a slot the API has already rejected can only fail again, so
  // the patient is sent back to the picker instead. A URL that carries no slot
  // at all (an old bookmark from before slots were identified) is treated the
  // same way rather than being submitted for the API to reject.
  const slotIsStale =
    !params.timeSlotId || isStaleSlotError(createAppointment.error);
  const handlePickAnotherTime = () => {
    if (window.history.length > 1) window.history.back();
    else navigate({ to: "/" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guards the Enter-key path, which bypasses the disabled submit button.
    if (slotIsStale) return;
    if (!isValid) {
      setTouched(
        new Set([
          "firstName",
          "lastName",
          "email",
          "phoneNumber",
          "dateOfBirth",
          "symptoms",
        ]),
      );
      return;
    }
    const storedZip = sessionStorage.getItem(STORAGE_KEYS.SEARCHED_ZIP_CODE);
    const searchedZipCode =
      storedZip && /^\d{5}$/.test(storedZip) ? storedZip : undefined;
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      userEmail: email,
      userPhoneNumber: form.phone.replace(/\D/g, ""),
      dateOfBirth: form.dateOfBirth,
      symptoms: form.symptoms,
      specialistId: params.specialistId,
      // All four fields describe the one slot the patient clicked. The API
      // re-derives the visit type from `timeSlotId` and rejects the booking if
      // it disagrees, so the visit type is derived here from the same slot's
      // location instead of from independent state.
      timeSlotId: params.timeSlotId,
      locationId: params.locationId,
      appointmentDate: params.appointmentDate,
      visitType: params.locationId
        ? VisitType.InPerson
        : VisitType.Telemedicine,
      ...(searchedZipCode ? { searchedZipCode } : {}),
      smsConsent,
    };
    authCoordinator.storePendingBooking(payload);
    authCoordinator.storePendingBookingSearch(
      params as Record<string, string | undefined>,
    );

    // First-time patients save their card before the appointment is created —
    // the API rejects appointment creation without a valid card on file. The
    // same path saves a replacement card, which detaches the old one.
    if (collectingCard) {
      setCardError(null);
      setSavingCard(true);
      const result = await cardRef.current?.confirm();
      setSavingCard(false);
      if (!result?.ok) {
        setCardError(result?.error ?? "We couldn't save your card.");
        return;
      }
      // Refresh the summary so that if appointment creation then fails, the
      // patient sees the card they just saved instead of an empty card form.
      setIsReplacingCard(false);
      await queryClient.invalidateQueries({ queryKey: paymentMethodQueryKey });
    }

    createAppointment.mutate(payload, {
      onSuccess: (appointmentId) => {
        authCoordinator.clearPendingBooking();
        navigate({ to: "/confirmation", search: { appointmentId }, replace: true });
      },
      onError: (err, vars) => {
        if (err instanceof AuthRequiredError || err instanceof SessionExpiredError) {
          authCoordinator.redirectToLoginWithEmail(vars.userEmail);
          return;
        }
        // The slot the patient is holding is gone or wrong — drop the cached
        // availability so the picker they go back to shows what's left.
        if (isStaleSlotError(err)) {
          queryClient.invalidateQueries({ queryKey: ["available-time-slots"] });
        }
        // Stale-token path: apiClient 401 interceptor has already hard-navigated
        // to /login. Keep the stored payload so post-login replay can fire.
        const status = (err as { response?: { status?: number } } | null | undefined)?.response?.status;
        if (status !== 401) {
          authCoordinator.clearPendingBooking();
        }
      },
    });
  };

  return (
    <main className="min-h-screen bg-background pb-20 font-sans">
      <BackNavigationBar />
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <h1 className="headline text-2xl sm:text-3xl text-foreground">
            Request Appointment
          </h1>

          <AppointmentDetailsCard
            doctorName={params.doctor ?? "Doctor"}
            appointmentDate={params.appointmentDate}
            locationAddress={params.locationAddress}
            isTelehealth={!params.locationId}
            timeZone={params.timeZone}
          />

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <Card>
              <CardContent className="p-5 sm:p-6">
                <h2 className="headline text-lg sm:text-xl text-foreground mb-4">
                  Patient Information
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label
                      htmlFor="firstName"
                      className="text-sm font-medium text-foreground"
                    >
                      First Name
                    </label>
                    <Input
                      id="firstName"
                      value={form.firstName}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, firstName: e.target.value }))
                      }
                      onBlur={blur("firstName")}
                      placeholder="First name"
                      autoComplete="given-name"
                      error={!!err("firstName")}
                    />
                    <FieldError message={err("firstName")} />
                  </div>
                  <div className="space-y-1">
                    <label
                      htmlFor="lastName"
                      className="text-sm font-medium text-foreground"
                    >
                      Last Name
                    </label>
                    <Input
                      id="lastName"
                      value={form.lastName}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, lastName: e.target.value }))
                      }
                      onBlur={blur("lastName")}
                      placeholder="Last name"
                      autoComplete="family-name"
                      error={!!err("lastName")}
                    />
                    <FieldError message={err("lastName")} />
                  </div>
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-foreground"
                  >
                    Email
                  </label>
                  {isLoggedIn && email ? (
                    <div className="rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-foreground">
                      {email}
                    </div>
                  ) : (
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, email: e.target.value }))
                      }
                      onBlur={blur("email")}
                      placeholder="you@example.com"
                      autoComplete="email"
                      error={!!err("email")}
                    />
                  )}
                  <FieldError message={err("email")} />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="phoneNumber"
                    className="text-sm font-medium text-foreground"
                  >
                    Phone Number
                  </label>
                  <Input
                    id="phoneNumber"
                    type="tel"
                    inputMode="numeric"
                    value={phoneMask(form.phone)}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 10),
                      }))
                    }
                    onBlur={blur("phoneNumber")}
                    placeholder="(555) 555-5555"
                    autoComplete="tel"
                    error={!!err("phoneNumber")}
                  />
                  <FieldError message={err("phoneNumber")} />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="dateOfBirth"
                    className="text-sm font-medium text-foreground"
                  >
                    Date of Birth
                  </label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={form.dateOfBirth}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, dateOfBirth: e.target.value }))
                    }
                    onBlur={blur("dateOfBirth")}
                    max={new Date().toISOString().slice(0, 10)}
                    autoComplete="bday"
                    error={!!err("dateOfBirth")}
                  />
                  <FieldError message={err("dateOfBirth")} />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="symptoms"
                    className="text-sm font-medium text-foreground"
                  >
                    Reason for Visit
                  </label>
                  <textarea
                    id="symptoms"
                    value={form.symptoms}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, symptoms: e.target.value }))
                    }
                    onBlur={blur("symptoms")}
                    rows={4}
                    maxLength={5000}
                    className={`w-full rounded-md border ${err("symptoms") ? "border-destructive" : "border-input"} bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-24`}
                    placeholder="Please describe your symptoms or reason for visit..."
                  />
                  <FieldError message={err("symptoms")} />
                </div>
              </CardContent>
            </Card>

            {(needsCard || hasCard) && (
              <Card>
                <CardContent className="p-5 sm:p-6 space-y-4">
                  <div>
                    <h2 className="headline text-lg sm:text-xl text-foreground">
                      Payment Method
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Your card is saved securely with Stripe. You'll be charged
                      a $50 approval fee only if the doctor confirms your
                      appointment; any remaining balance is charged after your
                      visit.
                    </p>
                    {/* The fee is refundable, but only outside the notice
                        window, so say so before the patient commits rather than
                        leaving them to find out at the cancel button. */}
                    <p className="text-sm text-muted-foreground mt-2">
                      Cancelling at least 24 hours before your appointment is
                      free and the $50 is refunded in full. Cancelling with less
                      notice, or not attending, means the $50 is kept.{" "}
                      <Link
                        to="/cancellation-policy"
                        className="text-primary underline"
                      >
                        Read the cancellation policy
                      </Link>
                      .
                    </p>
                  </div>

                  {hasCard && paymentMethod && !isReplacingCard && (
                    <>
                      <SavedCardSummary card={paymentMethod} />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setCardError(null);
                          setIsReplacingCard(true);
                        }}
                      >
                        Replace card
                      </Button>
                    </>
                  )}

                  {collectingCard && (
                    <>
                      <CardCollector ref={cardRef} onError={setCardError} />
                      {isReplacingCard && (
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setIsReplacingCard(false);
                              setCardError(null);
                            }}
                            disabled={savingCard}
                          >
                            Keep current card
                          </Button>
                        </div>
                      )}
                    </>
                  )}

                  {cardError && (
                    <p className="text-sm text-destructive">{cardError}</p>
                  )}
                </CardContent>
              </Card>
            )}

            <label
              htmlFor="sms-consent"
              className="flex items-start gap-3 p-4 rounded-lg border border-border cursor-pointer transition-colors bg-card"
            >
              <input
                id="sms-consent"
                type="checkbox"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
                className="mt-0.5 size-4 rounded border-primary text-primary accent-primary focus:ring-primary"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground">
                  Text me about my appointment
                </span>
                <span className="block text-muted-foreground mt-0.5">
                  I agree to receive text messages from Apptly Health about my appointment,
                  scheduling, and related updates. Recurring messages. Msg & data rates
                  may apply. Reply STOP to unsubscribe, HELP for help.
                </span>
              </span>
            </label>

            {slotIsStale ? (
              <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm font-medium space-y-3">
                <p>
                  That appointment time is no longer available — it may have
                  just been booked by someone else. Please choose another time.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePickAnotherTime}
                >
                  Choose another time
                </Button>
              </div>
            ) : (
              createAppointment.error &&
              !(createAppointment.error instanceof AuthRequiredError) &&
              !(createAppointment.error instanceof SessionExpiredError) && (
                <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm font-medium">
                  {getErrorMessage(createAppointment.error)}
                </div>
              )
            )}

            {!isValid && (
              <p className="text-sm text-muted-foreground">
                Please complete:{" "}
                <span className="font-medium text-foreground">
                  {[
                    fieldErrors.firstName && "First Name",
                    fieldErrors.lastName && "Last Name",
                    fieldErrors.email && "Email",
                    fieldErrors.phoneNumber && "Phone Number",
                    fieldErrors.dateOfBirth && "Date of Birth",
                    fieldErrors.symptoms && "Reason for Visit",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={
                !isValid ||
                !paymentResolved ||
                savingCard ||
                slotIsStale ||
                createAppointment.isPending
              }
            >
              {savingCard
                ? "Saving card..."
                : createAppointment.isPending
                  ? "Processing..."
                  : "Request Appointment"}
            </Button>
          </form>
        </div>
      </div>
    </main>
  );
}
